import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Sparkles, Gift, Star, X } from "lucide-react";
import { useApp, API } from "../context";

/**
 * "You Got a Gift" login celebration.
 * - Polls /marketplace-shop/gifts/pending on auth
 * - Full-screen modal with confetti reveal
 * - Supports stacked gifts (Next button cycles)
 * - Claim marks notification as claimed + read
 */
const RARITY_GLOW = {
  common: "#94A3B8",
  uncommon: "#22C55E",
  rare: "#3B82F6",
  epic: "#A855F7",
  legendary: "#F59E0B",
};

const Confetti = () => {
  const pieces = Array.from({ length: 40 });
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {pieces.map((_, i) => {
        const left = Math.random() * 100;
        const delay = Math.random() * 0.8;
        const dur = 2.2 + Math.random() * 1.5;
        const colors = ["#F472B6", "#FBBF24", "#22D3EE", "#A855F7", "#34D399", "#FB7185"];
        const c = colors[i % colors.length];
        const size = 6 + Math.random() * 10;
        const rotate = Math.random() * 360;
        return (
          <span
            key={i}
            style={{
              position: "absolute",
              top: "-20px",
              left: `${left}%`,
              width: `${size}px`,
              height: `${size}px`,
              background: c,
              transform: `rotate(${rotate}deg)`,
              borderRadius: i % 2 ? "2px" : "50%",
              animation: `gift-fall ${dur}s ${delay}s cubic-bezier(.2,.6,.4,1) forwards`,
              opacity: 0.9,
              boxShadow: `0 0 12px ${c}99`,
            }}
          />
        );
      })}
      <style>{`
        @keyframes gift-fall {
          0% { transform: translateY(-40px) rotate(0deg); opacity: 1; }
          100% { transform: translateY(110vh) rotate(720deg); opacity: 0.2; }
        }
        @keyframes gift-pop {
          0% { transform: scale(0.3); opacity: 0; }
          60% { transform: scale(1.08); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes gift-glow {
          0%,100% { box-shadow: 0 0 24px currentColor, 0 0 48px currentColor; }
          50% { box-shadow: 0 0 48px currentColor, 0 0 96px currentColor; }
        }
      `}</style>
    </div>
  );
};

const GiftCelebrationModal = () => {
  const { user } = useApp();
  const navigate = useNavigate();
  const [gifts, setGifts] = useState([]);
  const [idx, setIdx] = useState(0);
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);

  const fetchPending = useCallback(async () => {
    if (!user || user.isDemo) return;
    const uid = user.id || user.email;
    try {
      const r = await axios.get(`${API}/marketplace-shop/gifts/pending/${uid}`);
      const g = r.data.gifts || [];
      if (g.length > 0) {
        setGifts(g);
        setIdx(0);
        setVisible(true);
      }
    } catch { /* silent */ }
  }, [user]);

  // Fire on login / user change. Small delay so it doesn't fight other modals.
  useEffect(() => {
    if (!user || user.isDemo) return;
    const t = setTimeout(() => fetchPending(), 1200);
    return () => clearTimeout(t);
  }, [user, fetchPending]);

  // Also re-check when user comes back to tab
  useEffect(() => {
    if (!user || user.isDemo) return;
    const onVis = () => { if (document.visibilityState === "visible") fetchPending(); };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [user, fetchPending]);

  const current = gifts[idx];

  const claimCurrent = async () => {
    if (!current || busy) return;
    setBusy(true);
    try {
      await axios.post(`${API}/marketplace-shop/gifts/claim`, {
        user_id: user.id || user.email,
        notif_id: current.id,
      });
    } catch { /* silent */ }
    setBusy(false);
    // Advance
    if (idx + 1 < gifts.length) {
      setIdx(idx + 1);
    } else {
      setVisible(false);
      setGifts([]);
    }
  };

  const dismissAll = async () => {
    if (!user || busy) return;
    setBusy(true);
    try {
      await axios.post(`${API}/marketplace-shop/gifts/claim-all/${user.id || user.email}`);
    } catch { /* silent */ }
    setBusy(false);
    setVisible(false);
    setGifts([]);
  };

  const viewInInventory = async () => {
    await claimCurrent();
    navigate("/marketplace?tab=inventory");
  };

  if (!visible || !current) return null;

  const rarity = (current.item_rarity || "common").toLowerCase();
  const glow = RARITY_GLOW[rarity] || RARITY_GLOW.common;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center px-4"
      data-testid="gift-celebration-modal"
      style={{ background: "rgba(8,10,22,0.85)", backdropFilter: "blur(8px)" }}
    >
      <Confetti />
      <div
        className="relative w-full max-w-md rounded-3xl border-2 overflow-hidden"
        style={{
          background: "linear-gradient(180deg, rgba(30,20,60,0.98), rgba(15,18,35,0.98))",
          borderColor: `${glow}55`,
          boxShadow: `0 20px 80px ${glow}44, 0 0 0 1px ${glow}22`,
          animation: "gift-pop 0.55s cubic-bezier(.2,.8,.3,1.2) forwards",
        }}
      >
        <button
          onClick={dismissAll}
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white/70 transition"
          data-testid="gift-celebration-close"
          aria-label="Close"
        >
          <X size={16} />
        </button>

        {/* Top ribbon */}
        <div
          className="px-6 py-3 text-center font-fredoka font-bold text-sm tracking-wide"
          style={{
            background: `linear-gradient(90deg, ${glow}33, ${glow}11, ${glow}33)`,
            color: glow,
            borderBottom: `1px solid ${glow}33`,
          }}
        >
          {gifts.length > 1 ? `GIFT ${idx + 1} OF ${gifts.length}` : "YOU GOT A GIFT!"}
        </div>

        <div className="p-7 text-center">
          <div className="text-white/60 text-xs uppercase tracking-[0.2em] font-bold mb-2 flex items-center justify-center gap-2">
            <Sparkles size={12} /> From {current.sender_name || "A friend"} <Sparkles size={12} />
          </div>

          <h2 className="text-3xl font-fredoka font-bold text-white mb-5 leading-tight">
            🎁 A gift just for you!
          </h2>

          {/* Item reveal */}
          <div
            className="mx-auto mb-5 w-32 h-32 rounded-3xl flex items-center justify-center text-7xl"
            style={{
              background: `radial-gradient(circle at 50% 40%, ${glow}33, ${glow}08)`,
              color: glow,
              border: `2px solid ${glow}66`,
              animation: "gift-glow 2.2s ease-in-out infinite",
            }}
            data-testid="gift-item-icon"
          >
            <span style={{ textShadow: `0 0 24px ${glow}` }}>{current.item_icon || "🎁"}</span>
          </div>

          <p className="text-white font-bold text-xl mb-1" data-testid="gift-item-name">
            {current.item_name || "A surprise item"}
          </p>
          <p
            className="text-xs uppercase tracking-wider font-bold mb-5"
            style={{ color: glow }}
          >
            {rarity} {current.item_section ? `· ${current.item_section}` : ""}
          </p>

          <p className="text-white/60 text-sm mb-6">
            It&apos;s already in your inventory, ready to use!
          </p>

          <div className="flex gap-2">
            <button
              onClick={viewInInventory}
              disabled={busy}
              className="flex-1 px-4 py-3 rounded-2xl font-fredoka font-bold text-sm text-white transition hover:scale-[1.02] disabled:opacity-50"
              style={{
                background: `linear-gradient(135deg, ${glow}, ${glow}AA)`,
                boxShadow: `0 8px 24px ${glow}44`,
              }}
              data-testid="gift-view-inventory-btn"
            >
              <Gift size={14} className="inline mr-1.5 -mt-0.5" />
              View in Inventory
            </button>
            <button
              onClick={claimCurrent}
              disabled={busy}
              className="px-4 py-3 rounded-2xl font-fredoka font-bold text-sm text-white/80 bg-white/10 hover:bg-white/15 transition disabled:opacity-50"
              data-testid="gift-next-btn"
            >
              {idx + 1 < gifts.length ? "Next →" : "Awesome!"}
            </button>
          </div>

          {gifts.length > 1 && (
            <div className="mt-4 flex items-center justify-center gap-1">
              {gifts.map((_, i) => (
                <span
                  key={i}
                  className="block w-2 h-2 rounded-full"
                  style={{ background: i === idx ? glow : "rgba(255,255,255,0.18)" }}
                />
              ))}
            </div>
          )}
        </div>

        <div className="px-5 py-2.5 text-center border-t border-white/10 text-white/40 text-[10px] font-bold uppercase tracking-widest flex items-center justify-center gap-1.5">
          <Star size={10} className="text-yellow-300" />
          Gifted by {current.sender_name || "A friend"}
          <Star size={10} className="text-yellow-300" />
        </div>
      </div>
    </div>
  );
};

export default GiftCelebrationModal;
